import {
  ApprovalAction,
  ApprovalOrder,
  ApprovalPolicy,
  ApprovalPolicyApprover,
  ApprovalSlot,
  ApprovalStateApprover,
  ApprovalStatePolicy,
  BlockedSlot,
  NextRequiredApprover,
  PositionHolder,
} from '@/types/approvals'
import { sortBySortOrder } from '@/utils/field'

/**
 * What one approver entry points at, without the payload's two nullable
 * columns. Everything that builds, compares or renders an approver list works
 * on these, so nothing has to re-decide which column a `kind` uses.
 */
export type ApproverRef =
  | { kind: 'user'; userId: string }
  | { kind: 'position'; positionId: string }

/** Stable across reorders, so it can key a row and compare two lists. */
export function approverRefKey(ref: ApproverRef): string {
  return ref.kind === 'user'
    ? `user:${ref.userId}`
    : `position:${ref.positionId}`
}

/** Null for an entry missing the id its own kind needs — bad data, not a row. */
function toApproverRef(approver: ApprovalPolicyApprover): ApproverRef | null {
  if (approver.kind === 'position') {
    return approver.position_id
      ? { kind: 'position', positionId: approver.position_id }
      : null
  }
  return approver.user_id ? { kind: 'user', userId: approver.user_id } : null
}

/**
 * Approvers in approval order. `sort_order` is the order, not array order, so
 * a shuffled list from the API still reads correctly. The spread matters:
 * sortBySortOrder sorts in place, and this runs on a prop.
 */
export function orderedApproverRefs(
  approvers: ApprovalPolicyApprover[],
): ApproverRef[] {
  const seen = new Set<string>()
  const sorted = sortBySortOrder([...approvers]) as ApprovalPolicyApprover[]

  return sorted.flatMap((approver) => {
    const ref = toApproverRef(approver)
    if (!ref) return []
    const key = approverRefKey(ref)
    if (seen.has(key)) return []
    seen.add(key)
    return [ref]
  })
}

/**
 * Only the people a policy names outright. A position entry has no user id —
 * who it asks is read from the ERP at the moment someone approves — so it
 * can't appear here.
 */
export function orderedUserIds(approvers: ApprovalPolicyApprover[]): string[] {
  return orderedApproverRefs(approvers).flatMap((ref) =>
    ref.kind === 'user' ? [ref.userId] : [],
  )
}

/**
 * sort_order comes from the index, so entries are dense and 0-based. Each
 * entry carries only the id its kind uses: the server rejects one that fills
 * both columns.
 */
export function toApproverEntries(
  refs: ApproverRef[],
): ApprovalPolicyApprover[] {
  return refs.map((ref, sort_order) =>
    ref.kind === 'user'
      ? { sort_order, kind: 'user' as const, user_id: ref.userId }
      : { sort_order, kind: 'position' as const, position_id: ref.positionId },
  )
}

/**
 * Everything one approver row renders, derived once so the approval card and
 * the policy dialogs can't describe the same approver differently.
 */
export type ApproverInfo = {
  /** The person or the position this row stands for. */
  ref: ApproverRef
  /**
   * Unique within one policy's rows. Not the ref: a position entry expands
   * into one row per slot, so one position (or even one holder, if they hold
   * more than one slot) can back several rows.
   */
  key: string
  /** 1-based position in the approver list. */
  stepNumber: number
  /** Null when the policy's config couldn't be read. */
  approvalOrder: ApprovalOrder | null
  /**
   * The approver's decision, or null for one they haven't made yet. Undefined
   * where the document doesn't have the policy applied — nothing decided,
   * nothing pending.
   */
  latestAction?: ApprovalAction | null
  /** Sequential only: this row's entry is the policy's `next_required`. */
  isCurrentTurn: boolean
  /** Sequential only: this approver's turn hasn't come up yet. */
  isFutureStep: boolean
  /** Any-order only: the policy is satisfied and this approver never acted. */
  notNeeded: boolean
  /** Set for a position row: the position's name. */
  positionName?: string
  /**
   * Set for a position row: what the slot resolved from, ready to show. Each
   * is the accounting system's readable name for the thing — a project or an
   * employee — falling back to the raw value it stores when there's no name.
   */
  startingPoints?: string[]
  /** Set for a position row: who the ERP reports as currently holding the slot. */
  holders?: PositionHolder[]
  /** Set for a position row that couldn't be filled: why, and how to fix it. */
  blocked?: BlockedSlot
  /** Set for a position row: the pd user ids who may act on this slot. */
  slotUserIds?: string[]
  /** Set for a position entry row: one summary per slot, in slot order. */
  slots?: EntrySlotInfo[]
}

/**
 * One slot's summary, used on a position's single entry row instead of
 * expanding into one row per slot. That row shows one avatar tile for the
 * whole position, so each slot's detail is summarized here rather than
 * split across separate rows.
 */
export type EntrySlotInfo = {
  /**
   * What the slot resolved from, ready to show. Each is the accounting
   * system's readable name for the thing — a project or an employee —
   * falling back to the raw value it stores when there's no name.
   */
  startingPoints: string[]
  /** Who the ERP reports as currently holding the slot. */
  holders: PositionHolder[]
  blocked?: BlockedSlot
}

/** The pd user id a row names, or undefined for a position row. */
export function approverRowUserId(row: ApproverInfo): string | undefined {
  return row.ref.kind === 'user' ? row.ref.userId : undefined
}

function isNextRequiredEntry(
  approver: ApprovalStateApprover,
  nextRequired: NextRequiredApprover | null,
): boolean {
  if (!nextRequired) return false
  if (nextRequired.kind === 'user') {
    return approver.kind === 'user' && approver.user_id === nextRequired.user_id
  }
  return (
    approver.kind === 'position' &&
    approver.position_id === nextRequired.position_id
  )
}

function slotStartingPoints(slot: ApprovalSlot): string[] {
  return slot.starting_points.map(
    (value) => slot.starting_point_labels?.[value] ?? value,
  )
}

function entryRef(approver: ApprovalStateApprover): ApproverRef {
  return approver.kind === 'user'
    ? { kind: 'user', userId: approver.user_id }
    : { kind: 'position', positionId: approver.position_id }
}

/**
 * -1 when `next_required` is null (any-order policy) or matches no approver
 * (bad data) — must not read as index 0.
 */
function currentEntryIndexOf(
  sorted: ApprovalStateApprover[],
  nextRequired: NextRequiredApprover | null,
): number {
  return sorted.findIndex((approver) =>
    isNextRequiredEntry(approver, nextRequired),
  )
}

/**
 * Rows for an applied policy: the approvers of the version in force, in
 * approval order. A position entry expands into one row per slot — any one
 * of a slot's current holders can act on it — so it can contribute more than
 * one row. Approval order counts entries, not slots, so every row from the
 * same position entry shares that entry's `stepNumber` and `isCurrentTurn`.
 */
export function statePolicyApproverRows(
  policy: ApprovalStatePolicy,
): ApproverInfo[] {
  const sorted = sortBySortOrder([
    ...policy.approvers,
  ]) as ApprovalStateApprover[]
  const isSequential = policy.approval_order === 'sequential'
  const currentEntryIndex = currentEntryIndexOf(sorted, policy.next_required)
  const hasCurrentStep = currentEntryIndex !== -1

  const rows: ApproverInfo[] = []

  sorted.forEach((approver, entryIndex) => {
    const stepNumber = entryIndex + 1
    const isCurrentTurn =
      isSequential && hasCurrentStep && entryIndex === currentEntryIndex
    const isFutureEntry =
      isSequential && hasCurrentStep && entryIndex > currentEntryIndex

    if (approver.kind === 'user') {
      rows.push({
        ref: entryRef(approver),
        key: `${approver.sort_order}:${approver.user_id}`,
        stepNumber,
        approvalOrder: policy.approval_order,
        latestAction: approver.latest_action,
        isCurrentTurn,
        isFutureStep: isFutureEntry && !approver.latest_action,
        notNeeded:
          policy.approval_order === 'any' &&
          policy.satisfied &&
          !approver.latest_action,
      })
      return
    }

    approver.slots.forEach((slot, slotIndex) => {
      rows.push({
        ref: entryRef(approver),
        key: `${approver.sort_order}:${approver.position_id}:${slotIndex}`,
        stepNumber,
        approvalOrder: policy.approval_order,
        latestAction: slot.latest_action,
        isCurrentTurn,
        isFutureStep: isFutureEntry && !slot.latest_action,
        notNeeded:
          policy.approval_order === 'any' &&
          policy.satisfied &&
          !slot.latest_action,
        positionName: approver.position_name,
        startingPoints: slotStartingPoints(slot),
        holders: slot.holders,
        blocked: slot.blocked ?? undefined,
        slotUserIds: slot.user_ids,
      })
    })
  })

  return rows
}

/**
 * One row per approver entry, positions included — for a view that only
 * needs to count approvers or show a generic mark per entry, not the detail
 * of who holds a position or which of its slots is open. A position entry
 * never carries a single `latestAction` of its own — pd-approval only
 * reports one per slot — so its row's `latestAction` is always undefined,
 * the same as for a policy the document hasn't applied.
 */
export function statePolicyApproverEntryRows(
  policy: ApprovalStatePolicy,
): ApproverInfo[] {
  const sorted = sortBySortOrder([
    ...policy.approvers,
  ]) as ApprovalStateApprover[]
  const isSequential = policy.approval_order === 'sequential'
  const currentEntryIndex = currentEntryIndexOf(sorted, policy.next_required)
  const hasCurrentStep = currentEntryIndex !== -1

  return sorted.map((approver, entryIndex) => {
    const latestAction =
      approver.kind === 'user' ? approver.latest_action : undefined
    const isCurrentTurn =
      isSequential && hasCurrentStep && entryIndex === currentEntryIndex
    const isFutureStep =
      isSequential &&
      hasCurrentStep &&
      entryIndex > currentEntryIndex &&
      !latestAction

    return {
      ref: entryRef(approver),
      key: `${approver.sort_order}:${
        approver.kind === 'user' ? approver.user_id : approver.position_id
      }`,
      stepNumber: entryIndex + 1,
      approvalOrder: policy.approval_order,
      latestAction,
      isCurrentTurn,
      isFutureStep,
      notNeeded:
        policy.approval_order === 'any' && policy.satisfied && !latestAction,
      ...(approver.kind === 'position'
        ? {
            positionName: approver.position_name,
            slots: approver.slots.map((slot) => ({
              startingPoints: slotStartingPoints(slot),
              holders: slot.holders,
              blocked: slot.blocked ?? undefined,
            })),
          }
        : {}),
    }
  })
}

/**
 * Rows for a policy the document hasn't applied: who it would ask, in order.
 * `latestAction` is left off, so the rows claim no progress.
 */
export function policyApproverRows(policy: ApprovalPolicy): ApproverInfo[] {
  return orderedApproverRefs(policy.approvers).map((ref, index) => ({
    ref,
    key: `${index}:${approverRefKey(ref)}`,
    stepNumber: index + 1,
    approvalOrder: policy.approval_order,
    isCurrentTurn: false,
    isFutureStep: false,
    notNeeded: false,
  }))
}
